import type { QuickCategory } from 'used-vehicle-contracts';
import { StatCard } from '../../components/ui/DefinitionList';
import { RangeScale } from '../../components/ui/RangeScale';
import { ScenarioCard } from '../../components/ui/ScenarioCard';
import { formatKm, formatMoney } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { quickCategoryTone } from './estimates-display';
import type { LocalQuickRange } from './valuation-local';

export interface QuickRangeCardProps {
  quickRange: LocalQuickRange | null;
  odometerKm: number | null;
  selected: QuickCategory | null;
  hasTemplate: boolean;
  onSelect: (key: string | null) => void;
}

/**
 * The quick vehicle price range (plan §5.11, §7 step 1).
 *
 * It is the first number a customer hears, and it is computed with **the real equations and the real
 * template values** — not a rule of thumb — so the indicative band and the detailed appraisal that
 * follows it cannot contradict each other. The salesperson may keep the closest scenario as an early
 * indicator; that is a *label*, saved with the estimate, and the inspection on the next step replaces
 * it with the actual figure.
 */
export const QuickRangeCard = ({ quickRange, odometerKm, selected, hasTemplate, onSelect }: QuickRangeCardProps) => {
  const { t } = useI18nTranslation();

  const scenarios = quickRange?.scenarios ?? [];
  const priced = scenarios.filter((scenario) => scenario.appraisal !== null);
  const selectedScenario = scenarios.find((scenario) => scenario.key === selected) ?? null;

  return (
    <section className="space-y-5 rounded-lg border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-card-foreground">{t('estimates.quickRangeTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('estimates.quickRangeSubtitle')}</p>
        </div>
        <div className="w-full sm:w-64">
          <StatCard
            label={t('estimates.quickRangeIndicative')}
            value={
              quickRange === null || quickRange.min === null || quickRange.max === null ? (
                t('common.notSet')
              ) : (
                <span
                  dir="ltr"
                  className="tabular-nums"
                >
                  {formatMoney(quickRange.min)} – {formatMoney(quickRange.max)}
                </span>
              )
            }
          />
        </div>
      </div>

      {!hasTemplate ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">{t('estimates.quickRangeNeedsTemplate')}</p>
      ) : priced.length === 0 ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">{t('estimates.quickRangeIncomplete')}</p>
      ) : (
        <>
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
            {t('estimates.quickRangeNotice', {
              entered: formatKm(odometerKm),
              expected: formatKm(quickRange?.expectedKm ?? null),
              band: quickRange?.band === null || quickRange?.band === undefined ? t('common.notSet') : t(`estimates.bands.${quickRange.band}`),
            })}
          </p>

          {quickRange !== null && quickRange.min !== null && quickRange.max !== null ? (
            <RangeScale
              min={quickRange.min}
              max={quickRange.max}
              minLabel={formatMoney(quickRange.min)}
              maxLabel={formatMoney(quickRange.max)}
              markers={priced.map((scenario) => ({
                key: scenario.key,
                value: scenario.appraisal!,
                label: `${t(`estimates.scenarios.${scenario.key}`)}: ${formatMoney(scenario.appraisal)}`,
              }))}
            />
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {scenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.key}
                tone={quickCategoryTone(scenario.key)}
                toneLabel={t(`estimates.conditions.${scenario.condition}`)}
                amount={formatMoney(scenario.appraisal)}
                label={t(`estimates.scenarios.${scenario.key}`)}
                caption={t('estimates.scenarioCaption', {
                  km: formatKm(scenario.km),
                  note: t(`estimates.scenarioNotes.${scenario.key}`),
                })}
                isSelected={selected === scenario.key}
                onSelect={() => onSelect(selected === scenario.key ? null : scenario.key)}
              />
            ))}
          </div>

          {quickRange !== null && quickRange.assumptions.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              {t('estimates.quickRangeAssumptions', {
                fields: quickRange.assumptions.map((assumption) => t(`estimates.assumptions.${assumption}`)).join(t('common.listSeparator')),
              })}
            </p>
          ) : null}

          {selectedScenario ? (
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t('estimates.quickRangeSelected', {
                label: t(`estimates.scenarios.${selectedScenario.key}`),
                amount: formatMoney(selectedScenario.appraisal),
              })}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('estimates.quickRangeSelectHint')}</p>
          )}
        </>
      )}

      <details className="rounded-md border border-border bg-muted px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium text-foreground">{t('estimates.quickRangeExplainTitle')}</summary>
        <p className="mt-2 text-sm text-muted-foreground">{t('estimates.quickRangeExplainBody')}</p>
      </details>
    </section>
  );
};
